import styled from "styled-components";
import { HiArrowRight } from "react-icons/hi2";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  padding: 2.5rem 0 2rem;

  @media only screen and (max-width: 840px) {
    padding: 2.5rem 1rem 2rem;
  }
`;

const Paragraph = styled.p`
  font-size: 1rem;
  line-height: 1.55;
  color: var(--color-text-secondary);

  text-align: justify;
  hyphens: none;
`;

const PageTitle = styled.h1`
  font-size: 1.5rem;
  font-weight: 500;
  line-height: 1.3;
  margin: 0 0 0.5rem 0;
  color: var(--color-secondary);
`;

const SectionTitle = styled.h2`
  font-size: 1.25rem;
  font-weight: 500;
  line-height: 1.35;
  margin: 1rem 0 0 0;
  color: var(--color-secondary);
`;

const CategoryTitle = styled.h3`
  font-size: 1rem;
  font-weight: 500;
  line-height: 1.55;
  margin: 0;
  color: var(--color-secondary);
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`;

const CategoryBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  margin-top: 0.5rem;
`;

const DonutBlock = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 2rem;
  margin: 0.5rem 0 1.5rem 0;

  @media only screen and (max-width: 640px) {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.5rem;
  }
`;

const Donut = styled.div`
  width: 160px;
  height: 160px;
  border-radius: 50%;
  flex-shrink: 0;
  position: relative;
  background: conic-gradient(
    var(--donut-1) 0% 34.899%,
    var(--donut-2) 34.899% 63.087%,
    var(--donut-3) 63.087% 93.960%,
    var(--donut-4) 93.960% 100%
  );
`;

const DonutCenter = styled.div`
  position: absolute;
  top: 25%;
  left: 25%;
  width: 50%;
  height: 50%;
  background: var(--color-background);
  border-radius: 50%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const DonutTotal = styled.span`
  font-size: 1.5rem;
  font-weight: 500;
  color: var(--color-secondary);
  line-height: 1;
`;

const DonutSubtitle = styled.span`
  font-size: 0.875rem;
  color: var(--color-text-secondary);
  margin-top: 0.125rem;
`;

const DonutLegend = styled.ul`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  list-style: none;
  padding: 0;
  margin: 0;
  flex: 1;
`;

const LegendItem = styled.li`
  display: flex;
  align-items: center;
  gap: 0.625rem;
  font-size: 1rem;
  line-height: 1.4;
  color: var(--color-text-secondary);
`;

const LegendSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 2px;
  background: ${props => props.$color};
  flex-shrink: 0;
`;

const LegendLabel = styled.span`
`;

const LegendDots = styled.span`
  flex: 1;
  height: 1em;
  border-bottom: 1px dotted var(--color-text-secondary);
  opacity: 0.4;
`;

const LegendCount = styled.span`
  font-weight: 500;
  color: var(--color-secondary);
  font-variant-numeric: tabular-nums;
`;

const ChipsRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.5rem;
  margin: 0.5rem 0 0 0;
`;

const Chip = styled.span`
  background: var(--chip-bg);
  color: var(--chip-text);
  font-weight: 500;
  font-size: 0.875rem;
  padding: 0.375rem 0.875rem;
  border-radius: 14px;
  white-space: nowrap;
`;

const BenthosLink = styled.a`
  align-self: flex-start;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0;
  margin-top: 1rem;

  font-size: 1rem;
  color: var(--color-secondary);
  font-weight: 500;

  transition: opacity 0.2s;

  &:hover {
    opacity: 0.75;
  }

  & svg {
    width: 1rem;
    height: 1rem;
  }
`;

function About() {
  return (
    <Container>
      <PageTitle>Sources</PageTitle>

      <Paragraph>
        Atcheza queries a curated knowledge base of public reference
        material on Arraial do Cabo, Brazil, and the cabista fishing
        community that lives there. The base is organized in four
        categories: legislation, academic research, journalism and
        culture. Scope, source types and exclusions for each category
        are documented below.
      </Paragraph>

      <Paragraph>
        Community-generated content, including recordings, contributions
        and observations, is governed by a separate sovereignty layer
        and is not present in this knowledge base. The one exception is
        first-person testimony from the documentary series Roots and
        Algorithms, which enters with the author's recorded consent and
        approved attribution, described under culture and oral memory
        below. Chat messages are not used as training material.
      </Paragraph>

      <Section>
        <SectionTitle>The four categories</SectionTitle>

        <DonutBlock>
          <Donut>
            <DonutCenter>
              <DonutTotal>149</DonutTotal>
              <DonutSubtitle>sources</DonutSubtitle>
            </DonutCenter>
          </Donut>
          <DonutLegend>
            <LegendItem>
              <LegendSwatch $color="var(--donut-1)" />
              <LegendLabel>Legislation, rights and frameworks</LegendLabel>
              <LegendDots />
              <LegendCount>52</LegendCount>
            </LegendItem>
            <LegendItem>
              <LegendSwatch $color="var(--donut-2)" />
              <LegendLabel>Academic research</LegendLabel>
              <LegendDots />
              <LegendCount>42</LegendCount>
            </LegendItem>
            <LegendItem>
              <LegendSwatch $color="var(--donut-3)" />
              <LegendLabel>Culture and oral memory</LegendLabel>
              <LegendDots />
              <LegendCount>46</LegendCount>
            </LegendItem>
            <LegendItem>
              <LegendSwatch $color="var(--donut-4)" />
              <LegendLabel>Journalism</LegendLabel>
              <LegendDots />
              <LegendCount>9</LegendCount>
            </LegendItem>
          </DonutLegend>
        </DonutBlock>

        <CategoryBlock>
          <CategoryTitle>
            Legislation, rights and frameworks
          </CategoryTitle>
          <Paragraph>
            Scope: Brazilian fisheries law, the RESEX Marinha de Arraial
            decree, the management plan and zoning, closed seasons,
            minimum sizes, fishing license rules and official complaint
            channels. Coverage extends to the rules for approaching and
            interacting with marine fauna during the whale season, the
            seguro-defeso benefit as updated by Lei 15.399/2026, and the
            municipal public-call process for cultural credentialing in
            Arraial do Cabo. The base also covers the Brazilian and
            international frameworks governing traditional knowledge
            and data protection: CBD Article 8(j), the Nagoya Protocol,
            FPIC under ILO Convention 169 and UNDRIP, Lei 13.123/2015,
            LGPD, GDPR, the CARE Principles and Local Contexts TK
            Labels.
          </Paragraph>
          <Paragraph>
            Sources include ICMBio, IBAMA, the Planalto portal, the
            Ministério do Meio Ambiente, the Prefeitura de Arraial do
            Cabo, AREMAC, Colônia Z-5, GIDA, the Local Contexts
            initiative and the UN Convention on Biological Diversity.
          </Paragraph>
        </CategoryBlock>

        <CategoryBlock>
          <CategoryTitle>Academic research</CategoryTitle>
          <Paragraph>
            Scope: peer-reviewed papers, theses and identification
            guides covering coastal ecology, ethnobotany, traditional
            boat-building, the Cabo Frio upwelling system, women in
            artisanal fishing, marine archaeology, macroalgae and
            commercial species of fish, crustaceans and molluscs.
          </Paragraph>
          <Paragraph>
            Sources include USP, UENF, UERJ, FGV, IFRJ, IEAPM, the
            Jardim Botânico do Rio de Janeiro and peer-reviewed
            journals.
          </Paragraph>
        </CategoryBlock>

        <CategoryBlock>
          <CategoryTitle>Journalism</CategoryTitle>
          <Paragraph>
            Scope: public reporting on RESEX governance, council
            elections, illegal fishing enforcement, scientific findings
            on the coast and the institutional history of fishing in
            the region.
          </Paragraph>
          <Paragraph>
            Sources include O ECO, Folha dos Lagos, UFF Press, the
            Polícia Federal, the Ministério Público Federal, ICMBio
            communications and other public press.
          </Paragraph>
        </CategoryBlock>

        <CategoryBlock>
          <CategoryTitle>
            Culture and oral memory · not listed publicly
          </CategoryTitle>
          <Paragraph>
            Scope: cabista terminology and dialect, festivals,
            traditional fishing arts, oral history and ethnoictiology of
            Arraial do Cabo. Some entries reference named
            tradition-bearers or carry location-specific knowledge such
            as offshore pesqueiros.
          </Paragraph>
          <Paragraph>
            The category includes first-person testimony recorded for
            the documentary series Roots and Algorithms, in which fishers
            of the region speak about their craft. Each testimony enters
            the base with the attribution its author approved and is
            cited under the person's own name, role and territory.
            Consent records live outside the knowledge base and every
            testimony remains revocable.
          </Paragraph>
          <Paragraph>
            This category is not enumerated publicly. It sits inside
            the boundary of CBD Article 8(j), Lei 13.123/2015 (Marco da
            Biodiversidade) and the CARE Principles. Each entry is
            reviewed against a research ethics protocol with the
            community. Items with named-individual content,
            location-specific knowledge or material under traditional
            knowledge protection are governed by revocable consent and
            are not published on this site. The ethics protocol itself
            is part of the base.
          </Paragraph>
        </CategoryBlock>
      </Section>

      <Section>
        <SectionTitle>Standards we curate against</SectionTitle>
        <ChipsRow>
          <Chip>FAIR Principles</Chip>
          <Chip>CARE Principles</Chip>
          <Chip>FPIC</Chip>
          <Chip>ILO 169 · UNDRIP</Chip>
          <Chip>CBD Art. 8(j)</Chip>
          <Chip>Nagoya Protocol</Chip>
          <Chip>Lei 13.123/2015</Chip>
          <Chip>LGPD · GDPR</Chip>
          <Chip>TK Labels (Local Contexts)</Chip>
        </ChipsRow>
        <Paragraph>
          These standards govern what enters the base, how each entry is attributed and what stays in the sovereignty layer.
        </Paragraph>
      </Section>

      <Section>
        <SectionTitle>How this base is curated</SectionTitle>
        <Paragraph>
          Curation is editorial, in partnership with the cabista fishing community of Arraial do Cabo. Sources are reviewed against the standards above and the scope documented for each category before inclusion. For the Culture category, each entry is reviewed with the community against a research ethics protocol that travels with the base. Consent for community-protected material is revocable, and items are removed when consent is withdrawn.
        </Paragraph>
      </Section>

      <BenthosLink
        href="https://benthos.ai"
        target="_blank"
        rel="noopener noreferrer"
      >
        Read about Benthos
        <HiArrowRight />
      </BenthosLink>
    </Container>
  );
}

export default About;
